import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { sanitizeHtml } from "../../utils/sanitize";
import "./ReplyEmail.css";
import { useNavigate, useParams } from "react-router-dom";
import { API_URL } from "../../config";
import { useAuth } from "../../providers/AuthContext";

const ReplyEmail = ({ email, onClose, isReplying }) => {
  console.log("The selected email is", email.account);
  const navigate = useNavigate();
  const { emailId } = useParams();
  const { user } = useAuth();
  console.log("the email is:", email);

  // Add logging to see what's coming from the backend
  console.log("Email data received:", email);

  // Extract draft more safely with multiple fallbacks and better debugging
  // Updated getDraftContent function for ReplyEmail.jsx

  const getDraftContent = () => {
    console.log("DETAILED EMAIL DATA:", JSON.stringify(email, null, 2));

    // First case: improved draft from the AI
    if (email?.draft?.improved_draft) {
      console.log(
        "Using email.draft.improved_draft:",
        email.draft.improved_draft
      );
      return email.draft.improved_draft;
    }

    // Second case: rewritten draft from the AI
    if (email?.rewritten_draft) {
      console.log("Using email.rewritten_draft:", email.rewritten_draft);
      return email.rewritten_draft;
    }

    // Third case: draft exists with draft property
    if (email?.draft?.draft) {
      console.log("Using email.draft.draft:", email.draft.draft);
      return email.draft.draft;
    }

    // Fourth case: direct content in draft (string)
    if (email?.draft && typeof email.draft === "string") {
      console.log("Using email.draft as string:", email.draft);
      return email.draft;
    }

    // Fifth case: draft as content property
    if (email?.draft?.content) {
      console.log("Using email.draft.content:", email.draft.content);
      return email.draft.content;
    }

    // ✅ NEW: Sixth case: draft_text from email object (from your API)
    if (email?.email?.draft_text) {
      console.log("Using email.email.draft_text:", email.email.draft_text);
      return email.email.draft_text;
    }

    // Seventh case: original draft from AI
    if (email?.draft?.original_draft?.draft) {
      console.log(
        "Using email.draft.original_draft.draft:",
        email.draft.original_draft.draft
      );
      return email.draft.original_draft.draft;
    }

    // Eighth case: direct content property on email
    if (email?.content && typeof email.content === "string") {
      console.log("Using email.content:", email.content);
      return email.content;
    }

    // Ninth case: check for messages with content
    if (
      email?.messages &&
      Array.isArray(email.messages) &&
      email.messages.length > 0
    ) {
      if (email.messages[0]?.content) {
        console.log(
          "Using email.messages[0].content:",
          email.messages[0].content
        );
        return email.messages[0].content;
      }
    }

    // ✅ NEW: Tenth case: Check if there's draft_text at the root level
    if (email?.draft_text) {
      console.log("Using email.draft_text:", email.draft_text);
      return email.draft_text;
    }

    console.log("No draft content found, using empty default");
    const defaultDraft = ``;
    return defaultDraft;
  };

  const initialDraft = getDraftContent();
  console.log("Initial draft content:", initialDraft);

  const [reply, setReply] = useState(initialDraft);
  const [subject, setSubject] = useState(`Re: ${email?.email?.subject || ""}`);
  const [accessToken, setAccessToken] = useState(null);
  const [accountId, setAccountId] = useState(email?.account?.id || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [userConfig, setUserConfig] = useState(null);
  const [selectedView, setSelectedView] = useState("email");
  const [wordCount, setWordCount] = useState(0);
  const [summary, setSummary] = useState("");

  useEffect(() => {
    try {
      // Get and process access token
      const token = Cookies.get("access_token");
      if (token) {
        try {
          // Ensure token has Bearer prefix
          const decodedToken = token.startsWith("Bearer ")
            ? token
            : `Bearer ${token}`;

          // Set axios default authorization header
          axios.defaults.headers.common["Authorization"] = decodedToken;
          setAccessToken(decodedToken); // Store the token with Bearer prefix

          // Fetch user configuration if user ID is available
          if (user?.id) {
            fetchUserConfig(user.id);
          }
        } catch (error) {
          console.error("Invalid token:", error);
        }
      }

      // Get account ID from email or from cookies
      if (email?.account?.id) {
        setAccountId(email.account.id);
      } else {
        const account = Cookies.get("account_id");
        if (account) {
          setAccountId(account);
        }
      }

      // Debug logging
      console.log("Token setup complete in ReplyEmail component");
    } catch (error) {
      console.error("Error processing tokens:", error);
    }
  }, [user, email]);

  // Fetch user configuration for email categorization
  const fetchUserConfig = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/user/get-config`, {
        withCredentials: true,
      });
      setUserConfig(response.data);
      console.log("User configuration loaded:", response.data);
    } catch (error) {
      console.error("Error fetching user configuration:", error);
    }
  };

  const modules = {
    toolbar: "#formatting-toolbar", // Point to our custom toolbar
  };

  const formats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
  ];

  const handleFormat = (format) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      quill.format(format, !quill.getFormat()[format]);
    }
  };

  const quillRef = useRef(null);
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? "0" + minutes : minutes;
    return `${hours}:${minutesStr} ${ampm}`;
  };

  // Calculate word count when reply changes
  useEffect(() => {
    const text = reply.replace(/<[^>]*>/g, ""); // Remove HTML tags
    const words = text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);
    setWordCount(words.length);
  }, [reply]);

  // Add more detailed logging when the email prop changes
  useEffect(() => {
    console.log("Email prop changed:", email);
    if (email) {
      console.log("Email properties:", {
        id: email.email?.id,
        subject: email.email?.subject,
        has_draft: !!email.draft,
        draft_type: email.draft ? typeof email.draft : "undefined",
        draft_content: email.draft?.draft_text,
        has_rewritten: !!email.rewritten_draft,
        category: email.category,
        account_id: email.account?.id,
      });

      // If the draft changes, update the reply
      const newDraftContent = getDraftContent();
      if (newDraftContent && newDraftContent !== reply) {
        console.log("Updating reply with new draft content");
        setReply(newDraftContent);
      }

      // Update accountId if available in the email
      if (email.account?.id && email.account.id !== accountId) {
        setAccountId(email.account.id);
      }
    }
  }, [email]);

  const handleSend = async () => {
    if (!email?.email?.id) {
      setSendError("Email ID not found");
      return;
    }

    if (!accountId) {
      setSendError("Account ID not found");
      return;
    }

    if (!user?.id) {
      setSendError("User ID not found. Please log in again.");
      return;
    }

    setSending(true);
    setSendError(null);

    try {
      console.log("Sending email with params:", {
        userId: user.id,
        accountId,
        emailId: email.email.id,
      });

      // Using the API endpoint you provided
      await axios.post(
        `${API_URL}/email/${user.id}/accounts/${accountId}/emails/${email.email.id}/send`,
        {
          content: reply,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      console.log("Email sent successfully");
      onClose();
    } catch (error) {
      console.error(
        "Error sending email:",
        error.response?.status,
        error.response?.data
      );
      if (error.response?.status === 401) {
        setSendError("Authentication error. Please try logging in again.");
        navigate("/");
      } else {
        setSendError(error.response?.data?.detail || "Failed to send email");
      }
    } finally {
      setSending(false);
    }
  };

  const handleCloseReply = () => {
    onClose();
  };

  const fetchEmailSummary = async () => {
    if (!user?.id || !accountId || !email?.email?.id) {
      console.error("Missing required IDs for summary fetch:", {
        userId: user?.id,
        accountId,
        emailId: email?.email?.id,
      });
      setSummary("Unable to load summary - missing required information.");
      return;
    }

    try {
      console.log("Fetching email summary with params:", {
        userId: user.id,
        accountId,
        emailId: email.email.id,
      });

      // Using the summarize API endpoint you provided
      const response = await axios.get(
        `${API_URL}/email/${user.id}/accounts/${accountId}/emails/${email.email.id}/summarize`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      console.log("Summary response:", response.data);
      const cleanedSummary =
        response.data.summary?.replace(/^Summary:\s*/i, "") ||
        "No summary available.";
      setSummary(cleanedSummary);
    } catch (error) {
      console.error("Error fetching email summary:", error);
      setSummary("Failed to load summary. Please try again later.");
    }
  };

  useEffect(() => {
    if (
      selectedView === "summary" &&
      user?.id &&
      accountId &&
      email?.email?.id
    ) {
      fetchEmailSummary();
    }
  }, [selectedView, user?.id, accountId, email?.email?.id]);

  return (
    <div className="h-screen flex flex-col bg-[#EEEEEE]">
      <div className="flex-shrink-0 p-4 bg-[#EEEEEE] border-b border-gray-300 flex items-center">
        <button
          onClick={handleCloseReply}
          className="mr-4 rounded-full text-[#1F9BFF] bg-[#1F9BFF33] hover:text-blue-500 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        <h2 className="text-xl font-bold text-[#1F9BFF]">Email Response</h2>
      </div>
      <div className="flex-1 p-6 overflow-hidden">
        {/* Main Content: Left and Right Sections */}
        <div className="flex gap-6 h-[calc(100vh-220px)]">
          {/* Left Side - Email Display with Tabs */}
          <div className="w-1/2 flex flex-col">
            <div className="flex mb-0 gap-0">
              <button
                onClick={() => setSelectedView("email")}
                className={`px-6 py-2 text-xs rounded-t-lg ${
                  selectedView === "email"
                    ? "bg-white text-gray-900 font-medium"
                    : "bg-[#EEEEEE] text-gray-600 hover:text-gray-900"
                }`}
              >
                Email
              </button>
              <button
                onClick={() => setSelectedView("summary")}
                className={`px-6 py-2 text-xs rounded-t-lg ${
                  selectedView === "summary"
                    ? "bg-white text-gray-900 font-medium"
                    : "bg-[#EEEEEE] text-gray-600 hover:text-gray-900"
                }`}
              >
                Summary
              </button>
            </div>
            <div className="flex-1 bg-white rounded-b-lg rounded-tr-lg shadow-md overflow-hidden flex flex-col">
              {selectedView === "email" ? (
                <div className="p-4 border-b border-gray-300">
                  <div className="text-gray-900 font-bold">
                    {email?.email?.subject || "No Subject"}
                  </div>
                  <div className="text-gray-900 text-sm">To: me</div>
                  <div className="text-gray-900 text-sm">
                    {email?.email?.send_time
                      ? formatTime(email.email.send_time)
                      : "Unknown time"}
                  </div>
                </div>
              ) : (
                <div className="p-4">
                  <div className="text-gray-900 font-bold">
                    {email?.email?.subject || "No Subject"}
                  </div>
                  <div className="text-gray-900 text-sm">
                    From: {email?.email?.from_email || "Unknown sender"}
                  </div>
                  <div className="text-gray-900 text-sm">To: Me</div>
                  <div className="text-gray-900 text-sm">
                    {email?.email?.send_time
                      ? formatTime(email.email.send_time)
                      : "Unknown time"}
                  </div>
                  <div className="border-b border-gray-300 mt-2"></div>
                  {summary ? (
                    <div className="text-gray-900 text-sm mt-5">{summary}</div>
                  ) : (
                    <div className="text-gray-500 text-sm mt-5 flex items-center justify-center h-32">
                      <svg
                        className="animate-spin h-6 w-6 mr-2 text-blue-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Loading summary...
                    </div>
                  )}
                </div>
              )}
              <div className="flex-1 p-4 overflow-auto">
                {selectedView === "email" && (
                  <div
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: email?.email?.body_html
                        ? sanitizeHtml(email.email.body_html)
                        : `<div class="text-gray-700">${
                            email?.email?.body || "No content available"
                          }</div>`,
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Right Side - Email Reply with Tab */}
          <div className="w-1/2 flex flex-col">
            <div className="flex mb-0">
              <div className="px-6 py-2 text-xs rounded-t-lg bg-white text-gray-900 font-medium">
                Response
              </div>
            </div>
            <div className="flex-1 bg-white rounded-b-lg rounded-tr-lg shadow-md overflow-hidden flex flex-col">
              {/* Email Header Information */}
              <div className="p-4 border-b border-gray-300">
                <div className="font-bold text-gray-900 mb-2">{subject}</div>
                <div className="text-gray-900 text-sm">
                  To: {email?.email?.from_name || "Unknown"} &lt;
                  {email?.email?.from_email || "unknown@example.com"}&gt;
                </div>
              </div>

              {/* Quill Editor Container */}
              <div className="flex-1 flex flex-col">
                {/* Editor Content */}
                <div className="flex-1">
                  <ReactQuill
                    theme="snow"
                    value={reply}
                    onChange={setReply}
                    modules={modules}
                    formats={formats}
                    ref={quillRef}
                  />
                </div>

                {/* Bottom Toolbar */}
                <div className="bottom-toolbar">
                  <div className="toolbar-left">
                    {/* AI Voice Icon */}
                    <button className="voice-button">
                      <img
                        src="/aivoice.svg"
                        alt="Logo"
                        className="h-8 mx-auto"
                      />
                    </button>

                    {/* Word Count */}
                    <span className="word-count">{wordCount} words</span>

                    {/* Custom Formatting Toolbar */}
                    <div id="formatting-toolbar" className="ql-toolbar ql-snow">
                      <span className="ql-formats">
                        <button className="ql-bold"></button>
                        <button className="ql-italic"></button>
                        <button className="ql-underline"></button>
                        <button className="ql-strike"></button>
                      </span>
                      <span className="ql-formats">
                        <button className="ql-list" value="ordered"></button>
                        <button className="ql-list" value="bullet"></button>
                      </span>
                      <span className="ql-formats">
                        <button className="ql-link"></button>
                        <button className="ql-clean"></button>
                      </span>
                    </div>
                  </div>

                  <div className="toolbar-right">
                    {sendError && (
                      <div className="text-red-500 text-sm mr-3">
                        {sendError}
                      </div>
                    )}
                    <button
                      onClick={handleSend}
                      disabled={sending}
                      className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${
                        sending
                          ? "bg-[#1F9BFF] cursor-not-allowed"
                          : "bg-[#1F9BFF] hover:bg-blue-500"
                      }`}
                    >
                      {sending ? "Sending..." : "Send Response"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReplyEmail;
